import React, { useState } from 'react';
import { ReactComponent as OptionsIcon } from '../../../../assets/icons/note-options-icon.svg';
import { NotesFormat } from '../../../../types';
import { getUserInitials } from '../../../../helpers/functions';
import Tags from './Tags';
import moment from 'moment';

interface Props {
  note: NotesFormat;
  onEdit: (value: NotesFormat) => void;
  onDelete: (id: number) => void;
  setIsEditing: (value: React.SetStateAction<boolean>) => void;
  hasWriteAccess: boolean;
}

const Note = ({ note, hasWriteAccess, onEdit, onDelete }: Props): React.ReactElement => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const isExpandable = note.message.length > 250;
  const [viewText, setViewText] = useState(isExpandable ? note.message.slice(0, 250) : note.message);
  const noteRef = React.useRef(null);

  const isExpanded = note.message.length === viewText.length;

  const timeString = moment(new Date(note.createdOn + " UTC")).format('MMMM Do YYYY, hh:mm A');

  const markets = note.markets?.split(',');
  const metrics = note.metrics?.split(',');
  const retailers = note.retailers?.split(',');

  const handleEdit = () => {
    onEdit(note);
  };

  const handleClickOutside = (event: Event) => {
    if (noteRef.current && !(noteRef.current as any).contains(event.target)) {
      setShowOptions(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  return (
    <div className="flex relative border-t border-borderGray box-border py-4">
      {note.isUserNote && hasWriteAccess ? (
        <div
          className="cursor-pointer absolute h-10 w-10 top-[15px] right-[20px] z-[5] flex justify-center items-center"
          onClick={() => setShowOptions(!showOptions)}
          ref={noteRef}
        >
          <OptionsIcon className="cursor-pointer" />
          {showOptions && (
            <div className="bg-white border border-borderGray box-border rounded-[3px] absolute right-0 top-full font-nunitoMedium p-[3px] shadow-notesOptionsShadow">
              <p className="px-4 hover:bg-opaqueNavy" onClick={handleEdit}>
                Edit
              </p>
              <p className="px-2 hover:bg-opaqueNavy" onClick={() => onDelete(note.noteId)}>
                Delete
              </p>
            </div>
          )}
        </div>
      ) : (
        ''
      )}
      <div
        className={`h-[30px] w-[30px]  rounded-full flex items-center justify-center mr-4`}
        style={{ backgroundColor: `${note.isUserNote ? 'navy' : note.userColor ? `${note.userColor}` : 'navy'}` }}
      >
        <span className="text-white font-nunitoBold text-xs">{getUserInitials(note.userName)}</span>
      </div>
      <div className="w-full">
        <div className="flex items-center justify-between relative">
          <p className="m-0 text-sm leading-[21px] tracking-[0.2px] font-nunitoMedium">
            {note.userName} <span className="text-xs text-[#727272] font-nunitoMedium ml-3">{timeString}</span>
          </p>
        </div>

        <p className="font-nunitoRegular text-sm leading-[19.1px] tracking-[0.2px] mt-2.5 w-[90%]">
          {viewText.split('\n').map((line, index) => {
            return (
              <>
                {line} {index !== viewText.split('\n').length - 1 && <br />}{' '}
              </>
            );
          })}
          {isExpandable && (
            <button
              className="ml-1 text-blue-600  hover:font-semibold"
              onClick={() => setViewText(isExpanded ? note.message.slice(0, 250) : note.message)}
            >
              {isExpanded ? 'Show less' : '...Show more'}
            </button>
          )}
          {note.isEdited ? <span className="text-xs text-[#959393] font-nunitoMedium ml-1">(edited)</span> : ''}
        </p>

        <div className="flex py-2">
          <Tags label="Market" filters={markets} />
          <Tags label="Retailer" filters={retailers} styles={{ padding: 'pl-2' }} />
          <Tags label="Metric" filters={metrics} styles={{ padding: 'pl-2' }} />
        </div>
      </div>
    </div>
  );
};

export default Note;
